import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Briefcase, Target, TrendingUp, TrendingDown, Minus, Star, Download,
  Loader2, Phone, Mail, Users, Globe, Monitor, Smartphone, Tablet,
  BarChart3, Save, Plus, X, MapPin,
} from 'lucide-react';

export function ReportPreviewDialog({ previewReport, pdfData, onClose, onSave }) {
  const reportRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [notes, setNotes] = useState('');
  const [actions, setActions] = useState([]);
  const [newAction, setNewAction] = useState('');
  const [phoneCalls, setPhoneCalls] = useState(0);
  const [otherContacts, setOtherContacts] = useState(0);
  const [saving, setSaving] = useState(false);
  const [gaEvents, setGaEvents] = useState([]);
  const [gaEventsError, setGaEventsError] = useState('');
  const [gaCallEvent, setGaCallEvent] = useState('');
  const [savingGaEvent, setSavingGaEvent] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  if (!previewReport || !pdfData) return null;

  // Format the report's actual period for section labels (replaces the
  // misleading hard-coded "(30 jours)" that no longer matches the real
  // window used by the backend).
  const fmt = (d) => {
    if (!d) return '';
    const dt = typeof d === 'string' ? new Date(d) : d;
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const periodStart = pdfData.report?.period_start || previewReport.period_start;
  const periodEnd = pdfData.report?.period_end || previewReport.period_end;
  const periodLabel = (periodStart && periodEnd)
    ? `${fmt(periodStart)} → ${fmt(periodEnd)}`
    : '30 jours';

  const initEdit = () => {
    setNotes(pdfData.technical_section?.consultant_notes || '');
    setActions(pdfData.technical_section?.technical_actions || []);
    setPhoneCalls(pdfData.manager_section?.phone_calls || 0);
    setOtherContacts(pdfData.manager_section?.other_contacts || 0);
    setGaCallEvent(pdfData.manager_section?.ga_call_event_name || '');
    setEditMode(true);
    const clientId = previewReport.client_id;
    if (clientId) {
      const qs = (periodStart && periodEnd) ? `?start=${encodeURIComponent(periodStart)}&end=${encodeURIComponent(periodEnd)}` : '';
      axios.get(`${API}/clients/${clientId}/ga-events${qs}`, { withCredentials: true })
        .then(({ data }) => {
          setGaEvents(data.events || []);
          setGaEventsError(data.error || '');
          if (data.call_event_name) setGaCallEvent(data.call_event_name);
        })
        .catch(() => setGaEventsError('Impossible de charger les événements GA4'));
    }
  };

  const saveGaCallEvent = async (eventName) => {
    setGaCallEvent(eventName);
    setSavingGaEvent(true);
    try {
      await axios.put(`${API}/clients/${previewReport.client_id}/ga-call-event`,
        { event_name: eventName }, { withCredentials: true });
      toast.success(eventName
        ? `Les appels compteront l'événement « ${eventName} » — fermez puis rouvrez le rapport pour recalculer`
        : 'Événement d\'appel GA4 retiré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSavingGaEvent(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (onSave) {
        await onSave(previewReport.id, {
          consultant_notes: notes,
          technical_actions: actions,
          phone_calls: phoneCalls,
          other_contacts: otherContacts,
        });
      }
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  };

  const addAction = () => {
    if (newAction.trim()) {
      setActions([...actions, newAction.trim()]);
      setNewAction('');
    }
  };

  const removeAction = (idx) => {
    setActions(actions.filter((_, i) => i !== idx));
  };

  const handleExportPdf = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      const el = reportRef.current;
      const dialog = el.closest('[role="dialog"]');
      const prev = dialog ? { overflow: dialog.style.overflow, maxH: dialog.style.maxHeight } : {};
      if (dialog) { dialog.style.overflow = 'visible'; dialog.style.maxHeight = 'none'; }

      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 800 });

      if (dialog) { dialog.style.overflow = prev.overflow; dialog.style.maxHeight = prev.maxH; }

      const imgW = 190, pageH = 277;
      const imgH = (canvas.height * imgW) / canvas.width;
      const pdf = new jsPDF('p', 'mm', 'a4');
      let left = imgH, pos = 10;
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 10, pos, imgW, imgH);
      left -= pageH;
      while (left > 0) { pos = left - imgH + 10; pdf.addPage(); pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 10, pos, imgW, imgH); left -= pageH; }

      const name = (previewReport.client_name || 'client').replace(/\s+/g, '_');
      pdf.save(`Rapport_${name}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch { window.print(); } finally { setExporting(false); }
  };

  const mgr = pdfData.manager_section || {};
  const DEVICE_ICONS = { DESKTOP: Monitor, MOBILE: Smartphone, TABLET: Tablet };

  return (
    <Dialog open={!!previewReport} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{previewReport.title}</DialogTitle>
        </DialogHeader>

        <div ref={reportRef} className="mt-3 space-y-5 bg-white" data-testid="report-pdf-content">
          {/* Header */}
          <div className="bg-[#002FA7] text-white p-5 rounded-lg">
            <h1 className="font-heading font-bold text-xl">{previewReport.title}</h1>
            <div className="flex justify-between mt-2 text-sm opacity-80">
              <span>Client : {previewReport.client_name}</span>
              <span>{new Date(previewReport.period_start).toLocaleDateString('fr-FR')} — {new Date(previewReport.period_end).toLocaleDateString('fr-FR')}</span>
            </div>
          </div>

          {/* ===== 1. CONTACTS DU MOIS ===== */}
          <div className="border border-neutral-200 rounded-lg p-5">
            <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#002FA7]" /> Contacts du mois
            </h2>
            {editMode ? (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-neutral-500 block mb-1">Formulaires (auto Gmail)</label>
                  <div className="bg-neutral-50 p-3 rounded text-center">
                    <p className="font-mono text-2xl font-bold text-[#002FA7]">{mgr.gmail_forms_auto || 0}</p>
                    <p className="text-xs text-neutral-400">Calculé automatiquement</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-neutral-500 block mb-1">Appels téléphoniques</label>
                  <Input type="number" min="0" value={phoneCalls} onChange={e => setPhoneCalls(parseInt(e.target.value) || 0)} className="text-center font-mono text-lg" data-testid="report-phone-calls" />
                  {(mgr.gbp_calls_auto || 0) > 0 ? (
                    <p className="text-[11px] text-emerald-600 mt-1" data-testid="report-gbp-calls-hint">
                      {mgr.gbp_calls_auto} appels détectés via Google Business Profile
                    </p>
                  ) : mgr.gbp_calls_note ? (
                    <p className="text-[11px] text-amber-600 mt-1" data-testid="report-gbp-calls-note">
                      {mgr.gbp_calls_note}
                    </p>
                  ) : null}
                  {(mgr.ga_calls_auto || 0) > 0 && (
                    <p className="text-[11px] text-emerald-600 mt-1" data-testid="report-ga-calls-hint">
                      {mgr.ga_calls_auto} appels détectés via le site (Analytics)
                    </p>
                  )}
                  <div className="mt-2">
                    <label className="text-[11px] text-neutral-500 block mb-1">
                      Événement GA4 comptant comme appel (clics tél. du site)
                    </label>
                    <select
                      value={gaCallEvent}
                      onChange={e => saveGaCallEvent(e.target.value)}
                      disabled={savingGaEvent || !!gaEventsError}
                      className="w-full text-xs border border-neutral-200 rounded px-2 py-1.5 bg-white"
                      data-testid="report-ga-call-event-select"
                    >
                      <option value="">— Aucun —</option>
                      {gaCallEvent && !gaEvents.some(ev => ev.event_name === gaCallEvent) && (
                        <option value={gaCallEvent}>{gaCallEvent}</option>
                      )}
                      {gaEvents.map(ev => (
                        <option key={ev.event_name} value={ev.event_name}>
                          {ev.event_name} ({ev.count})
                        </option>
                      ))}
                    </select>
                    {gaEventsError && (
                      <p className="text-[11px] text-amber-600 mt-1" data-testid="report-ga-events-error">{gaEventsError}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-neutral-500 block mb-1">Autres contacts</label>
                  <Input type="number" min="0" value={otherContacts} onChange={e => setOtherContacts(parseInt(e.target.value) || 0)} className="text-center font-mono text-lg" data-testid="report-other-contacts" />
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-5 gap-3">
                  <ContactCard icon={Mail} value={mgr.gmail_forms_auto || mgr.leads_this_month || 0} label="Formulaires" color="text-[#002FA7]" />
                  <ContactCard icon={Phone} value={mgr.phone_calls || 0} label="Appels" color="text-emerald-600" />
                  <ContactCard icon={MapPin} value={mgr.gbp_directions_auto || 0} label="Itinéraires" color="text-sky-600" />
                  <ContactCard icon={Users} value={mgr.other_contacts || 0} label="Autres" color="text-amber-600" />
                  <ContactCard icon={Briefcase} value={mgr.total_contacts || (mgr.gmail_forms_auto || mgr.leads_this_month || 0) + (mgr.phone_calls || 0) + (mgr.other_contacts || 0)} label="Total contacts" color="text-neutral-900" bold />
                </div>
                {!exporting && (mgr.phone_calls || 0) === 0 && mgr.gbp_calls_note && (
                  <p className="text-[11px] text-amber-600 mt-2 flex items-center gap-1" data-testid="report-gbp-calls-note-view">
                    <Phone className="w-3 h-3" /> Appels : {mgr.gbp_calls_note}
                  </p>
                )}
              </>
            )}
          </div>

          {/* ===== 2. POSITIONS / MANAGER ===== */}
          <div className="bg-[#002FA7]/5 border border-[#002FA7]/20 rounded-lg p-5">
            <h2 className="font-heading font-semibold text-lg text-[#002FA7] mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" /> Positions SEO
            </h2>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <StatCard value={`+${mgr.keywords_improved || 0}`} label="Améliorées" color="text-green-600" />
              <StatCard value={`-${mgr.keywords_declined || 0}`} label="En baisse" color="text-red-500" />
              <StatCard value={pdfData.technical_section?.total_keywords || 0} label="Mots-clés suivis" color="text-[#002FA7]" />
            </div>
            {mgr.priority_keywords?.length > 0 && (
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-neutral-700 mb-2">Mots-clés prioritaires</h3>
                <SimpleKeywordTable keywords={mgr.priority_keywords} />
              </div>
            )}
          </div>

          {/* ===== 3. EVOLUTION DES POSITIONS ===== */}
          {pdfData.evolution_section?.keywords?.length > 0 && (
            <div className="border border-neutral-200 rounded-lg p-5">
              <h2 className="font-heading font-semibold text-base text-neutral-900 mb-3">
                Évolution des positions ({pdfData.evolution_section.month_labels?.join(' → ')})
              </h2>
              <EvolutionTable keywords={pdfData.evolution_section.keywords} monthLabels={pdfData.evolution_section.month_labels} />
            </div>
          )}

          {/* ===== 4. SEARCH CONSOLE ===== */}
          {pdfData.gsc_section?.keywords?.length > 0 && (
            <div className="border border-neutral-200 rounded-lg p-5">
              <h2 className="font-heading font-semibold text-base text-neutral-900 mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#002FA7]" /> Google Search Console ({periodLabel})
              </h2>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <StatCard value={pdfData.gsc_section.totals?.clicks?.toLocaleString('fr-FR') || 0} label="Clics" color="text-[#002FA7]" />
                <StatCard value={pdfData.gsc_section.totals?.impressions?.toLocaleString('fr-FR') || 0} label="Impressions" color="text-neutral-700" />
                <StatCard value={`${pdfData.gsc_section.totals?.avg_ctr || 0}%`} label="CTR moyen" color="text-[#2A9D8F]" />
              </div>
              {/* Devices */}
              {pdfData.gsc_section.devices?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-neutral-500 mb-2 uppercase tracking-wide">Appareils</h3>
                  <div className="flex gap-4">
                    {pdfData.gsc_section.devices.map(d => {
                      const Icon = DEVICE_ICONS[d.device] || Globe;
                      const total = pdfData.gsc_section.devices.reduce((s, x) => s + x.clicks, 0);
                      const pct = total > 0 ? Math.round((d.clicks / total) * 100) : 0;
                      return (
                        <div key={d.device} className="flex items-center gap-2 text-sm">
                          <Icon className="w-4 h-4 text-neutral-400" />
                          <span className="capitalize text-neutral-700">{d.device.toLowerCase()}</span>
                          <span className="font-mono font-medium">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Top keywords */}
              <h3 className="text-xs font-semibold text-neutral-500 mb-2 uppercase tracking-wide">Top mots-clés</h3>
              <GscTable keywords={pdfData.gsc_section.keywords} />
            </div>
          )}

          {/* ===== 5. GOOGLE ANALYTICS ===== */}
          {pdfData.ga_section && Object.keys(pdfData.ga_section).length > 0 && (
            <div className="border border-neutral-200 rounded-lg p-5">
              <h2 className="font-heading font-semibold text-base text-neutral-900 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#002FA7]" /> Google Analytics ({periodLabel})
              </h2>
              <div className="grid grid-cols-4 gap-3">
                <StatCard value={pdfData.ga_section.sessions?.toLocaleString('fr-FR') || 0} label="Sessions" color="text-[#002FA7]" />
                <StatCard value={pdfData.ga_section.users?.toLocaleString('fr-FR') || 0} label="Utilisateurs" color="text-neutral-700" />
                <StatCard value={(pdfData.ga_section.page_views ?? pdfData.ga_section.pageviews ?? 0).toLocaleString('fr-FR')} label="Pages vues" color="text-[#2A9D8F]" />
                <StatCard value={pdfData.ga_section.bounce_rate != null ? `${Math.round(pdfData.ga_section.bounce_rate)}%` : '—'} label="Taux rebond" color="text-amber-600" />
              </div>
            </div>
          )}

          {/* ===== 6. COIN TECHNIQUE DU CONSULTANT ===== */}
          <div className="border-2 border-[#002FA7]/30 rounded-lg p-5 bg-[#002FA7]/3">
            <h2 className="font-heading font-semibold text-lg text-[#002FA7] mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5" /> Coin technique du consultant
            </h2>
            {editMode ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">Observations et notes techniques</label>
                  <Textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={5}
                    placeholder="Décrivez les actions réalisées, observations SEO, recommandations..."
                    className="resize-y"
                    data-testid="report-consultant-notes"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-2">Actions réalisées ce mois</label>
                  <div className="space-y-2">
                    {actions.map((a, i) => (
                      <div key={`action-${a.slice(0,20)}`} className="flex items-center gap-2 bg-white rounded px-3 py-2 border">
                        <span className="text-sm flex-1">{a}</span>
                        <button onClick={() => removeAction(i)} className="text-neutral-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input value={newAction} onChange={e => setNewAction(e.target.value)} placeholder="Ex: Optimisation des balises meta..." onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addAction())} data-testid="report-new-action" />
                      <Button variant="outline" size="sm" onClick={addAction}><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {pdfData.technical_section?.consultant_notes ? (
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-neutral-700 mb-1">Notes et observations</h3>
                    <p className="text-sm text-neutral-700 whitespace-pre-wrap bg-white rounded p-3 border">{pdfData.technical_section.consultant_notes}</p>
                  </div>
                ) : (
                  <p className="text-sm text-neutral-400 italic mb-3">Aucune note technique — cliquez "Modifier" pour ajouter</p>
                )}
                {pdfData.technical_section?.technical_actions?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-700 mb-2">Actions réalisées</h3>
                    <ul className="space-y-1">
                      {pdfData.technical_section.technical_actions.map((a, i) => (
                        <li key={`ta-${a.slice(0,20)}`} className="text-sm text-neutral-700 bg-white rounded px-3 py-2 border flex items-start gap-2">
                          <span className="text-[#002FA7] font-bold">•</span>{a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-neutral-400 pt-3 border-t border-neutral-100">
            Rapport généré le {new Date().toLocaleDateString('fr-FR')} — SEO Platform
          </div>
        </div>

        {/* Actions bar */}
        <div className="flex justify-between pt-3 border-t mt-2 gap-2 flex-wrap">
          <div className="flex gap-2">
            <Button className="bg-[#002FA7] hover:bg-[#002277]" onClick={handleExportPdf} disabled={exporting} data-testid="report-download-pdf-btn">
              {exporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération...</> : <><Download className="w-4 h-4 mr-2" />Télécharger PDF</>}
            </Button>
            {editMode ? (
              <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="report-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            ) : (
              <Button variant="outline" onClick={initEdit} data-testid="report-edit-btn">
                Modifier
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ContactCard({ icon: Icon, value, label, color, bold }) {
  return (
    <div className="bg-white p-3 rounded text-center border border-neutral-100">
      <Icon className="w-4 h-4 mx-auto mb-1 text-neutral-400" />
      <p className={`font-mono text-2xl ${bold ? 'font-black' : 'font-bold'} ${color}`}>{value}</p>
      <p className="text-xs text-neutral-500 mt-1">{label}</p>
    </div>
  );
}

function StatCard({ value, label, color }) {
  return (
    <div className="bg-white p-3 rounded text-center border border-neutral-100">
      <p className={`font-mono text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-neutral-500 mt-1">{label}</p>
    </div>
  );
}

function SimpleKeywordTable({ keywords }) {
  return (
    <div className="bg-white rounded overflow-hidden border border-neutral-100">
      <table className="w-full text-sm">
        <thead><tr className="bg-neutral-50">
          <th className="text-left py-2 px-3 font-medium">Mot-clé</th>
          <th className="text-right py-2 px-3 font-medium">Position</th>
          <th className="text-right py-2 px-3 font-medium">Évol.</th>
        </tr></thead>
        <tbody>
          {keywords.map(kw => (
            <tr key={kw.keyword} className="border-t border-neutral-100">
              <td className="py-2.5 px-3 align-middle leading-relaxed">{kw.keyword}</td>
              <td className="py-2.5 px-3 text-right font-mono align-middle leading-relaxed">{kw.current_position || '—'}</td>
              <td className="py-2.5 px-3 text-right align-middle leading-relaxed"><ChangeBadge current={kw.current_position} previous={kw.previous_position} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EvolutionTable({ keywords, monthLabels }) {
  return (
    <div className="bg-white rounded overflow-hidden border border-neutral-100">
      <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '38%' }} />
          {monthLabels?.map((m, i) => <col key={`col-${i}`} style={{ width: `${42 / Math.max(monthLabels.length, 1)}%` }} />)}
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
        </colgroup>
        <thead><tr className="bg-neutral-50">
          <th className="text-left py-2 px-3 font-medium">Mot-clé</th>
          {monthLabels?.map(m => <th key={m} className="text-right py-2 px-2 font-normal text-xs text-neutral-500">{m}</th>)}
          <th className="text-right py-2 px-3 font-medium">Actuelle</th>
          <th className="text-right py-2 px-3 font-medium">Évol.</th>
        </tr></thead>
        <tbody>
          {keywords.map(kw => (
            <tr key={kw.keyword} className="border-t border-neutral-100">
              <td className="py-2.5 px-3 align-middle leading-snug">
                <span className="flex items-start gap-1">
                  {kw.is_priority && <Star className="w-3 h-3 text-amber-500 fill-amber-500 flex-shrink-0 mt-0.5" />}
                  <span className="text-xs break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }} title={kw.keyword}>
                    {kw.keyword}
                  </span>
                </span>
              </td>
              {kw.monthly_positions?.map((pos, idx) => (
                <td key={`${kw.keyword}-${idx}`} className="py-2.5 px-2 text-right font-mono text-sm align-middle leading-relaxed">
                  <PositionBadge value={pos} />
                </td>
              ))}
              <td className="py-2.5 px-3 text-right font-mono font-bold align-middle leading-relaxed"><PositionBadge value={kw.current_position} bold /></td>
              <td className="py-2.5 px-3 text-right font-mono text-xs align-middle leading-relaxed"><EvolutionBadge change={kw.change} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GscTable({ keywords }) {
  return (
    <div className="bg-white rounded overflow-hidden border border-neutral-100">
      <table className="w-full text-sm">
        <thead><tr className="bg-neutral-50">
          <th className="text-left py-2 px-3 font-medium">Mot-clé</th>
          <th className="text-right py-2 px-3 font-medium">Clics</th>
          <th className="text-right py-2 px-3 font-medium">Impr.</th>
          <th className="text-right py-2 px-3 font-medium">CTR</th>
          <th className="text-right py-2 px-3 font-medium">Pos.</th>
        </tr></thead>
        <tbody>
          {keywords.slice(0, 15).map(kw => (
            <tr key={kw.keyword} className="border-t border-neutral-100">
              <td className="py-2.5 px-3 align-middle leading-snug">
                <span className="block text-xs break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }} title={kw.keyword}>
                  {kw.keyword}
                </span>
              </td>
              <td className="py-2.5 px-3 text-right font-mono font-medium text-[#002FA7] align-middle leading-relaxed">{kw.clicks}</td>
              <td className="py-2.5 px-3 text-right font-mono text-neutral-600 align-middle leading-relaxed">{kw.impressions?.toLocaleString('fr-FR')}</td>
              <td className="py-2.5 px-3 text-right font-mono text-[#2A9D8F] align-middle leading-relaxed">{kw.ctr}%</td>
              <td className="py-2.5 px-3 text-right font-mono align-middle leading-relaxed">{kw.position}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PositionBadge({ value, bold }) {
  if (value === null || value === undefined) return <span className="text-neutral-300">—</span>;
  const cls = value <= 3 ? 'text-emerald-600' : value <= 10 ? 'text-[#002FA7]' : 'text-neutral-600';
  return <span className={`${cls} ${bold ? 'font-bold' : ''}`}>{value}</span>;
}

function ChangeBadge({ current, previous }) {
  if (!current || !previous) return <span className="text-neutral-400">—</span>;
  const diff = previous - current;
  if (diff > 0) return <span className="text-green-600 flex items-center justify-end gap-1"><TrendingUp className="w-3 h-3" />+{diff}</span>;
  if (diff < 0) return <span className="text-red-500 flex items-center justify-end gap-1"><TrendingDown className="w-3 h-3" />{diff}</span>;
  return <span className="text-neutral-400"><Minus className="w-3 h-3 inline" /></span>;
}

function EvolutionBadge({ change }) {
  if (change === null || change === undefined) return <span className="text-neutral-400">—</span>;
  if (change > 0) return <span className="text-emerald-600 flex items-center justify-end gap-1"><TrendingUp className="w-3 h-3" />+{change}</span>;
  if (change < 0) return <span className="text-red-500 flex items-center justify-end gap-1"><TrendingDown className="w-3 h-3" />{change}</span>;
  return <span className="text-neutral-400"><Minus className="w-3 h-3 inline" /></span>;
}
